// Web Push helpers for SCS. Registers the service worker on demand
// and manages a per-user push subscription with the backend.
import { api } from "@/api";

const SW_URL = "/service-worker.js";

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export function pushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

export function pushPermission() {
    if (!("Notification" in window)) return "unsupported";
    return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function ensureRegistration() {
    if (!pushSupported()) throw new Error("Push notifications aren't supported on this browser.");
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_URL, { scope: "/" });
}

export async function getExistingSubscription() {
    if (!pushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return null;
    return reg.pushManager.getSubscription();
}

export async function enablePush() {
    if (!pushSupported()) {
        throw new Error("This browser doesn't support push notifications. On iPhone, install the app from Share → Add to Home Screen first.");
    }
    // Must be called from a user gesture (click handler) — the caller ensures that.
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error("Notifications were blocked. Enable them for SCS in your browser or phone settings.");
    }

    const reg = await ensureRegistration();
    await navigator.serviceWorker.ready;

    const { data } = await api.get("/push/config");
    const publicKey = data.vapidPublicKey;
    if (!publicKey) throw new Error("Server is missing the notification key (VAPID). Ping support.");

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
    }
    // Send the raw subscription JSON to the backend
    await api.post("/push/subscribe", sub.toJSON());
    return sub;
}

export async function disablePush() {
    const sub = await getExistingSubscription();
    if (!sub) return;
    try {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    } catch (_) { /* still try to unsubscribe locally */ }
    try { await sub.unsubscribe(); } catch (_) {}
}

export async function sendTestPush() {
    const { data } = await api.post("/push/test");
    if (data && data.ok === false) {
        const reason = data.detail || "Test push couldn't be sent.";
        const err = new Error(reason);
        err.reason = data.reason;
        throw err;
    }
    return data;
}


// ---------- Chime + preferences ----------
const CHIME_PREF_KEY = "cleanflow.chime.enabled";

export function getChimePref() {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem(CHIME_PREF_KEY);
    return v === null ? true : v === "1"; // default ON
}

export function setChimePref(enabled) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CHIME_PREF_KEY, enabled ? "1" : "0");
}

// A soft two-note "ding-ding" chime (no asset needed).
export async function playChime() {
    if (typeof window === "undefined") return;
    if (!getChimePref()) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    try {
        const ctx = new Ctx();
        const now = ctx.currentTime;
        const tone = (freq, start, dur = 0.18, peak = 0.16) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, now + start);
            gain.gain.exponentialRampToValueAtTime(peak, now + start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
            osc.connect(gain).connect(ctx.destination);
            osc.start(now + start);
            osc.stop(now + start + dur + 0.02);
        };
        // C6 then E6 — friendly, distinctive, not shrill
        tone(1046.5, 0);
        tone(1318.5, 0.15);
        // Auto-close so we don't leak audio contexts
        setTimeout(() => { try { ctx.close(); } catch (_) {} }, 700);
    } catch (_) { /* ignore */ }
}

let _swListenerBound = false;
export function bindPushMessageListener() {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    if (_swListenerBound) return;
    _swListenerBound = true;
    navigator.serviceWorker.addEventListener("message", (event) => {
        const data = event.data || {};
        if (data.type === "cleanflow-push") {
            // App is open — play a chime and try a quick vibrate as a bonus.
            playChime();
            try {
                if (navigator.vibrate) navigator.vibrate([180, 90, 180]);
            } catch (_) {}
        }
    });
}
